import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { ToggleGroupSingleProps } from '@radix-ui/react-toggle-group'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { useReducedMotion } from 'motion/react'
import { ReactNode, Ref, useEffect, useRef, useState } from 'react'

import { SizeOptions } from '../../types/types'
import { AstraTooltip } from '../AstraTooltip/AstraTooltip'
import { astraTwMerge } from '../Utilities/astraTwMerge'

type SegmentedControlRootProps<T extends string = string> = {
    value?: T
    defaultValue?: T
    onValueChange?: (value: T) => void
    size?: SizeOptions
    iconsOnly?: boolean
    noUnstableEffects?: boolean
    ref?: Ref<HTMLDivElement>
} & Omit<ToggleGroupSingleProps, 'type'>

const segmentedControlRootStyles =
    'group/root relative isolate w-fit  min-w-max flex gap-1 rounded-md bg-neutral-rest p-0.5 text-center align-top overflow-hidden'
const indicatorStyles =
    "pointer-events-none absolute top-[1px] left-0 before:absolute before:inset-[1px] before:rounded before:bg-surface-base before:shadow before:content-['']"
const indicatorAnimationStyles = 'transition-all duration-150 ease-in'

const indicatorSizeStyles: Record<SizeOptions, string> = {
    small: 'before:h-4',
    medium: 'before:h-6',
    large: 'before:h-8',
}

const AstraSegmentedControlRoot = <T extends string = string>({
    ref,
    children,
    value: valueProp,
    defaultValue: defaultValueProp,
    onValueChange: onValueChangeProp,
    size = 'medium',
    noUnstableEffects = false,
    ...props
}: SegmentedControlRootProps<T>) => {
    // avoid animation if user has reduced motion
    const shouldReduceMotion = useReducedMotion()
    const [value, setValue] = useControllableState<T>({
        prop: valueProp,
        onChange: onValueChangeProp,
        defaultProp: defaultValueProp as T,
    })

    const containerRef = useRef<HTMLDivElement>(null)
    const [indicatorPosition, setIndicatorPosition] = useState({ left: 0, width: 0 })

    useEffect(() => {
        if (!containerRef.current || !value) return

        const selectedButton = containerRef.current.querySelector(`[data-state="on"]`)
        if (!selectedButton) return

        const containerRect = containerRef.current.getBoundingClientRect()
        const buttonRect = selectedButton.getBoundingClientRect()

        setIndicatorPosition({
            left: buttonRect.left - containerRect.left,
            width: buttonRect.width,
        })
    }, [value, children])

    return (
        <ToggleGroupPrimitive.Root
            data-disabled={props.disabled || undefined}
            ref={node => {
                containerRef.current = node
                if (typeof ref === 'function') {
                    ref(node)
                } else if (ref) {
                    ref.current = node
                }
            }}
            className={segmentedControlRootStyles}
            onValueChange={value => {
                if (value) {
                    setValue(value as T)
                }
            }}
            {...props}
            type="single"
            value={value}
            asChild={false}
            disabled={!!props.disabled}
        >
            {children}
            <div
                className={astraTwMerge(
                    `${indicatorStyles} ${indicatorSizeStyles[size]} ${shouldReduceMotion || noUnstableEffects ? '' : indicatorAnimationStyles}`
                )}
                style={{
                    transform: `translateX(${indicatorPosition.left}px)`,
                    width: `${indicatorPosition.width}px`,
                }}
            />
        </ToggleGroupPrimitive.Root>
    )
}

type SegmentedControlItemWrapperProps = {
    tooltipContent: string
    children: ReactNode
    showTooltip?: boolean
}

/**
 * Wraps the given children with a tooltip if `showTooltip` is true.
 *
 * @param tooltipContent - The content to display inside the tooltip.
 * @param children - The React node(s) to be rendered inside the wrapper.
 * @param showTooltip - Optional flag to determine whether to show a tooltip.
 */
const SegmentedControlItemWrapper = ({ children, showTooltip, tooltipContent }: SegmentedControlItemWrapperProps) => {
    if (!showTooltip) return children

    return <AstraTooltip tooltipContent={tooltipContent}>{children}</AstraTooltip>
}

type SegmentedControlItemOwnProps = {
    value: string
    size?: SizeOptions
    icon?: ReactNode
    iconOnly?: boolean
    noUnstableEffects?: boolean
    ref?: Ref<HTMLButtonElement>
} & ToggleGroupPrimitive.ToggleGroupItemProps

const labelSizeStyles = {
    small: 'h-4 text-xs ',
    medium: 'h-6 text-sm',
    large: 'h-8 text-sm',
}

const itemStyles = 'group peer flex select-none items-stretch'
const seperatorStyles =
    'my-[2px] w-[1px] bg-neutral-selected group-first:opacity-0 group-data-[state=on]:opacity-0 mx-[-0.5px] group-data-[sibling=true]:opacity-0 '
const labelStyles = 'flex grow px-3  items-center justify-center rounded transition-all hover:bg-neutral-hover'
const labelActiveStyles =
    'whitespace-nowrap z-elevated rounded flex items-center gap-2 overflow-hidden text-center opacity-0 group-data-[state=on]:opacity-100 transition-all delay-75 font-semibold'
const labelActiveBackgroundStyles = 'bg-surface-base shadow'
const labelInactiveStyles =
    'whitespace-nowrap opacity-1 absolute transition-opacity group-data-[state=on]:opacity-0 delay-75 text-muted px-3 overflow-hidden flex items-center gap-2 group-disabled:text-disabled'

/**
 * A single item component for the `AstraSegmentedControl` using Radix UI's ToggleGroupPrimitive.Item.
 *
 * @param children - The label or content to display inside the segmented control item.
 * @param size - The size of the item. Defaults to `'medium'`.
 * @param icon - Optional icon to display alongside the label.
 * @param iconOnly - If `true`, only the icon is shown and the label is presented in a tooltip.
 * @param props - Additional props passed to the underlying ToggleGroupPrimitive.Item, such as `value` and `disabled`.
 */
const AstraSegmentedControlItem = ({ ref, children, size = 'medium', icon, iconOnly, value, noUnstableEffects, ...props }: SegmentedControlItemOwnProps) => {
    const shouldReduceMotion = useReducedMotion()

    return (
        <ToggleGroupPrimitive.Item
            data-test={`segmented-control-item-${value}`}
            ref={ref}
            className={itemStyles}
            value={value}
            {...props}
            disabled={props.disabled}
            asChild={false}
            onClick={event => {
                event.currentTarget.parentElement?.querySelectorAll('button').forEach((el: HTMLElement) => {
                    el.setAttribute('data-sibling', 'false')
                    // @ts-expect-error this prop does exist
                    event.currentTarget.nextSibling?.setAttribute('data-sibling', 'true')
                })
            }}
        >
            <span className={seperatorStyles} />
            <SegmentedControlItemWrapper showTooltip={iconOnly} tooltipContent={String(children)}>
                <span className={labelStyles}>
                    <span
                        className={`${labelActiveStyles} ${labelSizeStyles[size]} ${shouldReduceMotion || noUnstableEffects ? labelActiveBackgroundStyles : ''}`}
                    >
                        {icon}
                        {!iconOnly && children}
                    </span>
                    <span className={`${labelInactiveStyles} ${labelSizeStyles[size]} `} aria-hidden>
                        {icon}
                        {!iconOnly && children}
                    </span>
                </span>
            </SegmentedControlItemWrapper>
        </ToggleGroupPrimitive.Item>
    )
}

type SegmentedControlProps<T extends string = string> = {
    controlOptions: {
        value: T
        label: string
        icon?: ReactNode
    }[]
} & SegmentedControlRootProps<T>

/**
 *
 * A reusable segmented control component designed for use within the Hero section of an application.
 * This component allows users to select a single option from multiple choices.
 *
 * @param {SegmentedControlProps} props - The props for the AstraSegmentedControl component.
 *
 * @param {React.Ref<HTMLDivElement>} forwardedRef - A forwarded ref for the root container element.
 *
 * Props:
 *
 * @property {string} size - Determines the size of the segmented control (e.g., small, medium, large).
 *
 * @property {Array<{ value: string; label: string }>} controlOptions - An array of options for the segmented control,
 *   where each option includes a `value` (unique identifier) and a `label` (display text).
 *
 * @property {...any} rootProps - Additional props passed to the root container of the component.
 *
 * @example
 * ```
 * <AstraSegmentedControl
 * defaultValue="option1"
 *  onValueChange={(value) => console.info(value)}
 *   size="medium"
 *   controlOptions={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' },
 *   ]}
 * />
 * ```
 *
 */
export const AstraSegmentedControl = <T extends string = string>({
    ref: forwardedRef,
    size,
    controlOptions,
    iconsOnly,
    noUnstableEffects: noUnstableEffects = false,
    ...props
}: SegmentedControlProps<T>) => (
    <AstraSegmentedControlRoot ref={forwardedRef} size={size} noUnstableEffects={noUnstableEffects} {...props}>
        {controlOptions.map(({ value, label, icon }) => (
            <AstraSegmentedControlItem size={size} value={value} icon={icon} iconOnly={iconsOnly} noUnstableEffects={noUnstableEffects} key={label}>
                {label}
            </AstraSegmentedControlItem>
        ))}
    </AstraSegmentedControlRoot>
)
